import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import './SystemActionsPage.css';
import Modal from './Modal'; // Import Modal component

const LEGACY_ORDER_CUTOFF_DATE = '2026-01-16T10:15:53.000Z';
const LEGACY_ORDER_CUTOFF_DATE_DISPLAY = new Date(LEGACY_ORDER_CUTOFF_DATE).toLocaleDateString('en-GB');

function SystemActionsPage() {
  const { currentUser, canPerformAction } = useAuth();
  const [isSyncingShopify, setIsSyncingShopify] = useState(false);
  const [isSyncingZoho, setIsSyncingZoho] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [error, setError] = useState('');
  const [repairOrderId, setRepairOrderId] = useState('6609299079392');
  const [isRepairingAllocation, setIsRepairingAllocation] = useState(false);
  const [zohoSyncEnabledUntil, setZohoSyncEnabledUntil] = useState(0); // ms epoch; allow Zoho sync within this window
  const [isClearingZohoToken, setIsClearingZohoToken] = useState(false);

  // New state for product sync
  const [isSyncingProducts, setIsSyncingProducts] = useState(null); // 'shopify'
  const [productSyncMessage, setProductSyncMessage] = useState('');
  const [productSyncError, setProductSyncError] = useState('');

  // New state for history
  const [shopifySyncHistory, setShopifySyncHistory] = useState([]);
  const [zohoHistory, setZohoHistory] = useState([]);
  const [showShopifyHistory, setShowShopifyHistory] = useState(false);
  const [showZohoHistory, setShowZohoHistory] = useState(false);
  const [isLoadingShopifyHistory, setIsLoadingShopifyHistory] = useState(false);
  const [isLoadingZohoHistory, setIsLoadingZohoHistory] = useState(false);
  const [canLoadMoreShopify, setCanLoadMoreShopify] = useState(true);
  const [canLoadMoreZoho, setCanLoadMoreZoho] = useState(true);

  // State for Purge Modal
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [purgeStep, setPurgeStep] = useState(1);
  const [purgeCount, setPurgeCount] = useState(0);
  const [isGettingCount, setIsGettingCount] = useState(false);
  const [purgeConfirmationText, setPurgeConfirmationText] = useState('');
  const [isPurging, setIsPurging] = useState(false);
  const [purgeSource, setPurgeSource] = useState(null); // null = all, 'Zoho' = only Zoho, 'Shopify' = only Shopify
  const [isPreviewingDuplicates, setIsPreviewingDuplicates] = useState(false);
  const [isExecutingDuplicates, setIsExecutingDuplicates] = useState(false);
  const [duplicatePreview, setDuplicatePreview] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateConfirmText, setDuplicateConfirmText] = useState('');
  const [isPreviewingArchiveRepair, setIsPreviewingArchiveRepair] = useState(false);
  const [isExecutingArchiveRepair, setIsExecutingArchiveRepair] = useState(false);
  const [archiveRepairPreview, setArchiveRepairPreview] = useState(null);
  const [showArchiveRepairModal, setShowArchiveRepairModal] = useState(false);
  const [archiveRepairConfirmText, setArchiveRepairConfirmText] = useState('');
  const [isPreviewingIdentityRepair, setIsPreviewingIdentityRepair] = useState(false);
  const [isExecutingIdentityRepair, setIsExecutingIdentityRepair] = useState(false);
  const [identityRepairPreview, setIdentityRepairPreview] = useState(null);
  const [showIdentityRepairModal, setShowIdentityRepairModal] = useState(false);
  const [identityRepairConfirmText, setIdentityRepairConfirmText] = useState('');

  const shopifyHistoryRef = useRef(shopifySyncHistory);
  shopifyHistoryRef.current = shopifySyncHistory;
  const zohoHistoryRef = useRef(zohoHistory);
  zohoHistoryRef.current = zohoHistory;
  const showShopifyHistoryRef = useRef(showShopifyHistory);
  showShopifyHistoryRef.current = showShopifyHistory;
  const showZohoHistoryRef = useRef(showZohoHistory);
  showZohoHistoryRef.current = showZohoHistory;
  const isSyncingRef = useRef({ shopify: false, zoho: false });
  isSyncingRef.current.shopify = isSyncingShopify;
  isSyncingRef.current.zoho = isSyncingZoho;

  const handleApiResponse = useCallback(async (response, context) => {
    if (!response.ok) {
      let errorMessage = `${context} failed with status ${response.status}.`;
      const contentType = response.headers.get('content-type') || '';
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch (e) {
        const looksLikeHtml = contentType.includes('text/html') || /^\s*<!doctype/i.test(errorText);
        if (looksLikeHtml) {
          errorMessage = `Server returned HTML instead of JSON for ${context}. This usually means the API route isn’t reaching the backend (functions not deployed) or the local backend/proxy isn’t running. Status ${response.status}.`;
        } else {
          errorMessage = `Server returned an invalid response for ${context}: ${errorText.substring(0, 150)}...`;
        }
      }
      throw new Error(errorMessage);
    }
    return response.json();
  }, []);

  const runRepairAllocation = useCallback(async ({ dryRun } = {}) => {
    const orderId = String(repairOrderId || '').trim();
    if (!orderId) {
      setError('Please enter an order id.');
      return;
    }
    if (isRepairingAllocation) return;
    try {
      setIsRepairingAllocation(true);
      setError('');
      setSyncMessage(dryRun ? 'Repair dry-run running...' : 'Repairing allocation evidence...');
      const token = await currentUser.getIdToken();
      const qs = new URLSearchParams();
      if (dryRun) qs.set('dryRun', 'true');
      qs.set('applyAllLines', 'true');
      const url = `/api/v1/admin/orders/${encodeURIComponent(orderId)}/repair-allocation?${qs.toString()}`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(dryRun ? {} : { 'Content-Type': 'application/json' }),
        },
        ...(dryRun ? {} : { body: JSON.stringify({ applyAllLines: true }) }),
      });
      const data = await handleApiResponse(resp, 'repair allocation');
      setSyncMessage(data?.message || (dryRun ? 'Dry-run completed.' : 'Repair completed.'));
    } catch (e) {
      setError(e?.message || 'Repair failed.');
    } finally {
      setIsRepairingAllocation(false);
    }
  }, [currentUser, handleApiResponse, isRepairingAllocation, repairOrderId]);

  const fetchHistory = useCallback(async (syncType, mode = 'initial') => {
    const isShopify = syncType === 'shopify';
    const limit = mode === 'initial' ? 5 : 15;
    const history = isShopify ? shopifyHistoryRef.current : zohoHistoryRef.current;
    const lastTimestamp = mode === 'more' && history.length > 0 ? history[history.length - 1].timestamp : null;

    const setIsLoading = isShopify ? setIsLoadingShopifyHistory : setIsLoadingZohoHistory;
    setIsLoading(true);
    
    try {
      const token = await currentUser.getIdToken();
      let url = `/api/v1/${syncType}/sync-history?limit=${limit}`;
      if (lastTimestamp) {
        url += `&lastTimestamp=${lastTimestamp}`;
      }
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await handleApiResponse(response, `fetch ${syncType} history`);
      
      const setHistory = isShopify ? setShopifySyncHistory : setZohoHistory;
      const setCanLoadMore = isShopify ? setCanLoadMoreShopify : setCanLoadMoreZoho;

      setHistory(prev => mode === 'more' ? [...prev, ...data.history] : data.history);
      setCanLoadMore(data.history.length === limit);

    } catch (err) {
      setError(`History Error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, handleApiResponse]);

  const handleToggleHistory = (syncType) => {
    const isShopify = syncType === 'shopify';
    const showHistory = isShopify ? showShopifyHistory : showZohoHistory;
    const setShowHistory = isShopify ? setShowShopifyHistory : setShowZohoHistory;
    const history = isShopify ? shopifySyncHistory : zohoHistory;

    if (!showHistory && history.length === 0) {
        fetchHistory(syncType, 'initial');
    }
    setShowHistory(prev => !prev);
  };

  const handleOrderSync = useCallback(async (syncType) => {
    const isShopify = syncType === 'shopify';
    isShopify ? setIsSyncingShopify(true) : setIsSyncingZoho(true);
    setSyncMessage('');
    setError('');

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/v1/${syncType}/sync-orders`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await handleApiResponse(response, `${syncType} sync`);
      setSyncMessage(data.message);
      if (isShopify) {
        // Allow Zoho sync for 1 minute after a successful Shopify sync.
        setZohoSyncEnabledUntil(Date.now() + 60 * 1000);
      }
      if (isShopify ? showShopifyHistoryRef.current : showZohoHistoryRef.current) {
        fetchHistory(syncType, 'initial');
      } else {
        (isShopify ? setShopifySyncHistory : setZohoHistory)([]);
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      isShopify ? setIsSyncingShopify(false) : setIsSyncingZoho(false);
    }
  }, [currentUser, handleApiResponse, fetchHistory]);

  const handleClearZohoTokenCache = async () => {
    if (!currentUser || isClearingZohoToken) return;
    const ok = window.confirm('Clear Zoho token cache? This will force a fresh token refresh on next Zoho request.');
    if (!ok) return;

    setIsClearingZohoToken(true);
    setError('');
    setSyncMessage('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/zoho/clear-token-cache', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await handleApiResponse(response, 'clear Zoho token cache');
      setSyncMessage(data.message || 'Zoho token cache cleared.');
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsClearingZohoToken(false);
    }
  };

  const handleProductSync = async (source) => {
    if (isSyncingProducts) return;

    const syncJobs = {
      shopify: [{ endpoint: '/api/v1/shopify/sync-products', name: 'Shopify Products' }],
      zoho: [{ endpoint: '/api/v1/zoho/sync-items', name: 'Zoho Products' }],
    };
    syncJobs.combined = [...syncJobs.shopify, ...syncJobs.zoho];

    const steps = syncJobs[source];
    if (!steps) return;

    setIsSyncingProducts(source);
    setProductSyncMessage(`Starting ${steps[0].name} sync...`);
    setProductSyncError('');

    try {
      const token = await currentUser.getIdToken();
      for (const step of steps) {
        setProductSyncMessage(`Starting ${step.name} sync...`);
        const response = await fetch(step.endpoint, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = await handleApiResponse(response, step.name);
        setProductSyncMessage(data.message || `${step.name} sync completed.`);
      }
    } catch (err) {
      setProductSyncError(`Error: ${err.message}`);
    } finally {
      setIsSyncingProducts(null);
    }
  };

  const handleOpenPurgeModal = async (sourceOverride = null) => {
    setPurgeSource(sourceOverride);
    setShowPurgeModal(true);
    setPurgeStep(1);
    setIsGettingCount(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/admin/purge-legacy-orders/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ cutoffDate: LEGACY_ORDER_CUTOFF_DATE, source: sourceOverride }),
      });
      const data = await handleApiResponse(response, 'purge preview');
      setPurgeCount(data.count);
      setPurgeStep(2);
    } catch (err) {
      setError(err.message);
      // Don't advance step on error
    } finally {
      setIsGettingCount(false);
    }
  };

  const handleConfirmPurge = async () => {
    if (purgeConfirmationText !== 'DELETE') {
      setError('You must type DELETE to confirm.');
      return;
    }
    setIsPurging(true);
    setError('');
    setSyncMessage('');

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/admin/purge-legacy-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ cutoffDate: LEGACY_ORDER_CUTOFF_DATE, source: purgeSource }),
      });
      const data = await handleApiResponse(response, 'purge execution');
      setSyncMessage(data.message); // Use syncMessage for success
      setShowPurgeModal(false);
      setPurgeConfirmationText('');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsPurging(false);
    }
  };

  const handleClosePurgeModal = () => {
    setShowPurgeModal(false);
    setPurgeConfirmationText('');
    setPurgeSource(null);
  };

  const handlePreviewDuplicateCleanup = async () => {
    if (!currentUser || isPreviewingDuplicates || isExecutingDuplicates) return;
    setIsPreviewingDuplicates(true);
    setError('');
    setSyncMessage('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/admin/orders/cleanup-duplicates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ maxGroups: 500 }),
      });
      const data = await handleApiResponse(response, 'duplicate cleanup preview');
      setDuplicatePreview(data);
      setShowDuplicateModal(true);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsPreviewingDuplicates(false);
    }
  };

  const handleExecuteDuplicateCleanup = async () => {
    if (!currentUser || isExecutingDuplicates) return;
    if (duplicateConfirmText !== 'CLEANUP') {
      setError('Type CLEANUP to confirm duplicate deletion.');
      return;
    }
    setIsExecutingDuplicates(true);
    setError('');
    setSyncMessage('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/admin/orders/cleanup-duplicates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ execute: true, maxGroups: 500 }),
      });
      const data = await handleApiResponse(response, 'duplicate cleanup execute');
      setSyncMessage(`Duplicate cleanup completed. Removed ${data.duplicateDocsRemoved || 0} duplicate docs from ${data.duplicateGroups || 0} groups.`);
      setShowDuplicateModal(false);
      setDuplicateConfirmText('');
      setDuplicatePreview(null);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsExecutingDuplicates(false);
    }
  };

  const handlePreviewArchiveRepair = async () => {
    if (!currentUser || isPreviewingArchiveRepair || isExecutingArchiveRepair) return;
    setIsPreviewingArchiveRepair(true);
    setError('');
    setSyncMessage('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/admin/orders/repair-archive-reentries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ limit: 250 }),
      });
      const data = await handleApiResponse(response, 'archive repair preview');
      setArchiveRepairPreview(data);
      setShowArchiveRepairModal(true);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsPreviewingArchiveRepair(false);
    }
  };

  const handleExecuteArchiveRepair = async () => {
    if (!currentUser || isExecutingArchiveRepair) return;
    if (archiveRepairConfirmText !== 'REPAIR') {
      setError('Type REPAIR to confirm archive reentry repair.');
      return;
    }
    setIsExecutingArchiveRepair(true);
    setError('');
    setSyncMessage('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/admin/orders/repair-archive-reentries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ execute: true, limit: 250 }),
      });
      const data = await handleApiResponse(response, 'archive repair execute');
      setSyncMessage(
        `BQ-Firestore overlap repair completed. Updated ${data.updatedCount || 0} overlapped orders.`
      );
      setShowArchiveRepairModal(false);
      setArchiveRepairConfirmText('');
      setArchiveRepairPreview(null);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsExecutingArchiveRepair(false);
    }
  };

  const handlePreviewIdentityRepair = async () => {
    if (!currentUser || isPreviewingIdentityRepair || isExecutingIdentityRepair) return;
    setIsPreviewingIdentityRepair(true);
    setError('');
    setSyncMessage('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/admin/orders/repair-identity-collisions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ limit: 500 }),
      });
      const data = await handleApiResponse(response, 'identity collision repair preview');
      setIdentityRepairPreview(data);
      setShowIdentityRepairModal(true);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsPreviewingIdentityRepair(false);
    }
  };

  const handleExecuteIdentityRepair = async () => {
    if (!currentUser || isExecutingIdentityRepair) return;
    if (identityRepairConfirmText !== 'REPAIR') {
      setError('Type REPAIR to confirm identity collision repair.');
      return;
    }
    setIsExecutingIdentityRepair(true);
    setError('');
    setSyncMessage('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/admin/orders/repair-identity-collisions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ execute: true, limit: 500 }),
      });
      const data = await handleApiResponse(response, 'identity collision repair execute');
      setSyncMessage(
        `Identity repair completed. Moved ${data.movedCount || 0}, recalculated ${data.recalculatedCount || 0} docs.`
      );
      setShowIdentityRepairModal(false);
      setIdentityRepairConfirmText('');
      setIdentityRepairPreview(null);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsExecutingIdentityRepair(false);
    }
  };

  // Tick while the local "Zoho allowed" window is active so the title/countdown updates.
  const [ticker, setTicker] = useState(0);
  useEffect(() => {
    const localWindowActive = zohoSyncEnabledUntil > Date.now();
    if (!localWindowActive) return undefined;

    const timer = window.setInterval(() => {
      if (zohoSyncEnabledUntil > Date.now()) {
        setTicker(t => t + 1);
      } else {
        clearInterval(timer);
        // Force one final render to clear the countdown
        setTicker(t => t + 1);
      }
    }, 500);
    return () => window.clearInterval(timer);
  }, [zohoSyncEnabledUntil]);

  const { isZohoSyncDisabled, zohoSyncTitle } = useMemo(() => {
    const nowMs = Date.now();
    const remainingMs = Math.max(0, zohoSyncEnabledUntil - nowMs);

    if (remainingMs > 0) {
      const secondsLeft = Math.ceil(remainingMs / 1000);
      return { isZohoSyncDisabled: false, zohoSyncTitle: `Sync Zoho now (expires in ${secondsLeft}s)` };
    }

    const lastSuccessfulShopifySync = shopifySyncHistory.find(entry => entry.status === 'Success');
    if (lastSuccessfulShopifySync) {
        const lastSyncTime = new Date(lastSuccessfulShopifySync.timestamp);
        const minutesSinceLastSync = (nowMs - lastSyncTime.getTime()) / (1000 * 60);
        if (minutesSinceLastSync <= 1) {
            return { isZohoSyncDisabled: false, zohoSyncTitle: 'Sync with Zoho' };
        }
    }
    return { isZohoSyncDisabled: true, zohoSyncTitle: 'Please sync with Shopify first, or sync Shopify again.' };
  }, [shopifySyncHistory, zohoSyncEnabledUntil, ticker]);

  return (
    <div className="system-actions-container">
      <h1>System Sync</h1>
      <div className="admin-section">
        <h2>Manual Order Syncing</h2>
        <p>
          Manually sync orders from external services into the local database. The system also syncs automatically in the background.
          <br/>
          <strong>Important:</strong> Always sync with Shopify first, then sync with Zoho within 1 minute.
        </p>
        <div className="sync-actions">
          <button onClick={() => handleOrderSync('shopify')} disabled={isSyncingShopify || isSyncingZoho} className="shopify-sync-btn">
            {isSyncingShopify ? 'Syncing...' : '1. Sync Shopify Orders'}
          </button>
          <button onClick={() => handleOrderSync('zoho')} disabled={isSyncingShopify || isSyncingZoho || isZohoSyncDisabled} title={zohoSyncTitle} className="zoho-sync-btn">
            {isSyncingZoho ? 'Syncing...' : '2. Sync Zoho Orders'}
          </button>
        </div>

        {(currentUser?.customClaims?.admin === true || canPerformAction('admin.syncOrders')) && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Repair Order Allocation (Restock)</div>
            <div className="sync-actions" style={{ gap: 8, flexWrap: 'wrap' }}>
              <input
                value={repairOrderId}
                onChange={(e) => setRepairOrderId(e.target.value)}
                placeholder="Order ID (Firestore doc id)"
                style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', minWidth: 260 }}
              />
              <button
                onClick={() => runRepairAllocation({ dryRun: true })}
                disabled={isRepairingAllocation || isSyncingShopify || isSyncingZoho}
                className="action-button"
                title="Dry-run: shows how many lines would be repaired."
              >
                {isRepairingAllocation ? 'Checking...' : 'Dry Run'}
              </button>
              <button
                onClick={() => runRepairAllocation({ dryRun: false })}
                disabled={isRepairingAllocation || isSyncingShopify || isSyncingZoho}
                className="action-button"
                title="Repairs missing inventory_status/fulfillment_status evidence from Batch Receive activity and recalculates v3."
              >
                {isRepairingAllocation ? 'Repairing...' : 'Repair Allocation'}
              </button>
            </div>
          </div>
        )}

        {canPerformAction('admin.clearZohoTokenCache') && (
          <div style={{ marginTop: 10 }}>
            <button
              onClick={handleClearZohoTokenCache}
              disabled={isSyncingShopify || isSyncingZoho || isClearingZohoToken}
              className="action-button"
              title="Clears cached Zoho OAuth token (in-memory + Firestore cache)."
            >
              {isClearingZohoToken ? 'Clearing...' : 'Clear Zoho Token Cache'}
            </button>
          </div>
        )}
        {syncMessage && <p className="sync-message success">{syncMessage}</p>}
        {error && <p className="sync-message error">{error}</p>}
      </div>

      <div className="admin-section">
        <h2>Product Syncing</h2>
        <p>Sync product catalogs from external services. Run this if you add new products or change product images.</p>
        <div className="sync-actions">
          <button onClick={() => handleProductSync('combined')} disabled={!!isSyncingProducts} className="shopify-sync-btn">
            {isSyncingProducts === 'combined' ? 'Syncing...' : 'Sync Shopify + Zoho Products'}
          </button>
          <button onClick={() => handleProductSync('shopify')} disabled={!!isSyncingProducts} className="shopify-sync-btn">
            {isSyncingProducts === 'shopify' ? 'Syncing...' : 'Sync Shopify Products'}
          </button>
          <button onClick={() => handleProductSync('zoho')} disabled={!!isSyncingProducts} className="zoho-sync-btn">
            {isSyncingProducts === 'zoho' ? 'Syncing...' : 'Sync Zoho Products'}
          </button>
        </div>
        <p className="action-description">
          Fetches products from Shopify and/or Zoho and refreshes the merged catalog.
        </p>
        {productSyncMessage && <p className="sync-message success">{productSyncMessage}</p>}
        {productSyncError && <p className="sync-message error">{productSyncError}</p>}
      </div>

      <div className="admin-section">
        <div className="history-header">
          <h2>Shopify Sync History</h2>
          <button onClick={() => handleToggleHistory('shopify')} className="toggle-history-btn">
            {showShopifyHistory ? 'Hide History' : 'Show History'}
          </button>
        </div>
        {showShopifyHistory && (
          <>
            {isLoadingShopifyHistory && shopifySyncHistory.length === 0 && <p>Loading history...</p>}
            <div className="history-table-wrapper">
              <table className="history-table">
                <thead>
                  <tr><th>Timestamp</th><th>Status</th><th>Orders Synced</th><th>Message</th></tr>
                </thead>
                <tbody>
                  {shopifySyncHistory.length > 0 ? shopifySyncHistory.map(entry => (
                    <tr key={entry.id}><td>{new Date(entry.timestamp).toLocaleString()}</td><td><span className={`status-tag ${entry.status.toLowerCase()}`}>{entry.status}</span></td><td>{entry.syncedOrdersCount ?? 'N/A'}</td><td className="message-cell">{entry.message}</td></tr>
                  )) : (<tr><td colSpan="4">No Shopify sync history found.</td></tr>)}
                </tbody>
              </table>
            </div>
            {canLoadMoreShopify && shopifySyncHistory.length > 0 && (<button onClick={() => fetchHistory('shopify', 'more')} className="load-more-btn" disabled={isLoadingShopifyHistory}>{isLoadingShopifyHistory ? 'Loading...' : 'Load More'}</button>)}
          </>
        )}
      </div>

      <div className="admin-section">
        <div className="history-header">
          <h2>Zoho Sync History</h2>
          <button onClick={() => handleToggleHistory('zoho')} className="toggle-history-btn">{showZohoHistory ? 'Hide History' : 'Show History'}</button>
        </div>
        {showZohoHistory && (
          <>
            {isLoadingZohoHistory && zohoHistory.length === 0 && <p>Loading history...</p>}
            <div className="history-table-wrapper">
              <table className="history-table">
                <thead>
                  <tr><th>Timestamp</th><th>Status</th><th>Orders Synced</th><th>Message</th></tr>
                </thead>
                <tbody>
                  {zohoHistory.length > 0 ? zohoHistory.map(entry => (
                    <tr key={entry.id}><td>{new Date(entry.timestamp).toLocaleString()}</td><td><span className={`status-tag ${entry.status.toLowerCase()}`}>{entry.status}</span></td><td>{entry.syncedOrdersCount ?? 'N/A'}</td><td className="message-cell">{entry.message}</td></tr>
                  )) : (<tr><td colSpan="4">No Zoho sync history found.</td></tr>)}
                </tbody>
              </table>
            </div>
            {canLoadMoreZoho && zohoHistory.length > 0 && (<button onClick={() => fetchHistory('zoho', 'more')} className="load-more-btn" disabled={isLoadingZohoHistory}>{isLoadingZohoHistory ? 'Loading...' : 'Load More'}</button>)}
          </>
        )}
      </div>

      {canPerformAction('admin.purgeLegacyOrders') && (
      <div className="admin-section danger-zone">
        <h2>Danger Zone</h2>
        <p>
          This action will permanently delete all orders from the database that were created on or before <strong>{LEGACY_ORDER_CUTOFF_DATE_DISPLAY}</strong>.
          This is intended as a one-time action to clean up legacy data.
        </p>
        <p><strong>This action is irreversible.</strong></p>
        <div className="sync-actions">
          <button
            onClick={() => handleOpenPurgeModal(null)}
            className="purge-btn"
            title="Delete all sources"
          >
            Purge Legacy Orders (All)
          </button>
          <button
            onClick={() => handleOpenPurgeModal('Zoho')}
            className="purge-btn secondary"
            title="Delete only Zoho orders"
          >
            Purge Zoho Legacy Orders
          </button>
        </div>
      </div>
      )}

      <div className="admin-section danger-zone">
        <h2>Duplicate Order Cleanup</h2>
        <p>
          Scans <code>all_orders</code>, selects canonical documents, archives duplicate payloads to BigQuery,
          remaps identity index to canonical docs, then deletes duplicates.
        </p>
        <p><strong>Run preview first, then execute once confirmed.</strong></p>
        <div className="sync-actions">
          <button
            onClick={handlePreviewDuplicateCleanup}
            className="purge-btn secondary"
            disabled={isPreviewingDuplicates || isExecutingDuplicates}
          >
            {isPreviewingDuplicates ? 'Previewing...' : 'Preview Duplicate Cleanup'}
          </button>
          <button
            onClick={() => setShowDuplicateModal(true)}
            className="purge-btn"
            disabled={isPreviewingDuplicates || isExecutingDuplicates || !duplicatePreview}
            title={!duplicatePreview ? 'Run preview first' : 'Execute duplicate cleanup'}
          >
            {isExecutingDuplicates ? 'Executing...' : 'Execute Cleanup'}
          </button>
        </div>
        {duplicatePreview && (
          <>
            <p className="action-description">
              Preview: {duplicatePreview.duplicateGroups || 0} groups, {duplicatePreview.duplicateDocs || 0} duplicate docs.
            </p>
            {Array.isArray(duplicatePreview.sampleDuplicates) && duplicatePreview.sampleDuplicates.length > 0 && (
              <div className="duplicate-sample-box">
                <strong>First 5 samples:</strong>
                <ul className="duplicate-sample-list">
                  {duplicatePreview.sampleDuplicates.slice(0, 5).map((item, idx) => (
                    <li key={`${item.identityKey}-${idx}`}>
                      <div><strong>Canonical:</strong> {item.canonicalDisplayOrder || item.canonicalDocId}</div>
                      <div><strong>Duplicates:</strong> {(item.duplicateDisplays || item.duplicateDocIds || []).join(', ')}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      <div className="admin-section danger-zone">
        <h2>BQ-Firestore Overlap Repair</h2>
        <p>
          Checks orders present in both Firestore and BigQuery archive, then updates Firestore docs using archived BQ data
          while preserving current operational fields.
        </p>
        <p><strong>No UI-only hiding. This updates real Firestore order documents.</strong></p>
        <div className="sync-actions">
          <button
            onClick={handlePreviewArchiveRepair}
            className="purge-btn secondary"
            disabled={isPreviewingArchiveRepair || isExecutingArchiveRepair}
          >
            {isPreviewingArchiveRepair ? 'Previewing...' : 'Preview Archive Repair'}
          </button>
          <button
            onClick={() => setShowArchiveRepairModal(true)}
            className="purge-btn"
            disabled={isPreviewingArchiveRepair || isExecutingArchiveRepair || !archiveRepairPreview}
            title={!archiveRepairPreview ? 'Run preview first' : 'Execute archive repair'}
          >
            {isExecutingArchiveRepair ? 'Executing...' : 'Execute Archive Repair'}
          </button>
        </div>
        {archiveRepairPreview && (
          <>
            <p className="action-description">
              Preview: overlap found {archiveRepairPreview.overlapCount || 0}.
            </p>
            {Array.isArray(archiveRepairPreview.sample) && archiveRepairPreview.sample.length > 0 && (
              <div className="duplicate-sample-box">
                <strong>First 5 samples:</strong>
                <ul className="duplicate-sample-list">
                  {archiveRepairPreview.sample.slice(0, 5).map((item, idx) => (
                    <li key={`${item.docId}-${idx}`}>
                      <div><strong>Order:</strong> {item.displayOrder || item.docId}</div>
                      <div><strong>Source:</strong> {item.source || 'unknown'}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      <div className="admin-section danger-zone">
        <h2>Identity Collision Repair</h2>
        <p>
          Detects orders where top-level IDs and <code>v3_identity</code> IDs disagree, then repairs canonical placement
          (safe moves for single-source collisions) and recalculates v3 projection.
        </p>
        <p><strong>Run preview first, then execute once confirmed.</strong></p>
        <div className="sync-actions">
          <button
            onClick={handlePreviewIdentityRepair}
            className="purge-btn secondary"
            disabled={isPreviewingIdentityRepair || isExecutingIdentityRepair}
          >
            {isPreviewingIdentityRepair ? 'Previewing...' : 'Preview Identity Repair'}
          </button>
          <button
            onClick={() => setShowIdentityRepairModal(true)}
            className="purge-btn"
            disabled={isPreviewingIdentityRepair || isExecutingIdentityRepair || !identityRepairPreview}
            title={!identityRepairPreview ? 'Run preview first' : 'Execute identity repair'}
          >
            {isExecutingIdentityRepair ? 'Executing...' : 'Execute Identity Repair'}
          </button>
        </div>
        {identityRepairPreview && (
          <>
            <p className="action-description">
              Preview: candidates {identityRepairPreview.candidateCount || 0}, moves {identityRepairPreview.moveCount || 0}, recalc-only {identityRepairPreview.recalcOnlyCount || 0}.
            </p>
            {Array.isArray(identityRepairPreview.sample) && identityRepairPreview.sample.length > 0 && (
              <div className="duplicate-sample-box">
                <strong>First 5 samples:</strong>
                <ul className="duplicate-sample-list">
                  {identityRepairPreview.sample.slice(0, 5).map((item, idx) => (
                    <li key={`${item.docId}-${idx}`}>
                      <div><strong>Doc:</strong> {item.docId}</div>
                      <div><strong>Mode:</strong> {item.mode}{item.targetDocId ? ` -> ${item.targetDocId}` : ''}</div>
                      <div><strong>Reason:</strong> {item.reason}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      {showPurgeModal && (
        <Modal isOpen={showPurgeModal} onClose={handleClosePurgeModal}>
          <h2>Confirm Permanent Deletion</h2>
          {error && <p className="error-message">{error}</p>}
          {purgeStep === 1 && (
            <p>{isGettingCount ? 'Calculating...' : 'Preparing to purge...'}</p>
          )}
          {purgeStep === 2 && (
            <div>
              <p>You are about to permanently delete <strong>{purgeCount}</strong> orders from the database.</p>
              <p>This action cannot be undone.</p>
              <div className="form-group">
                <label htmlFor="purge-confirm">To confirm, please type <strong>DELETE</strong> into the box below.</label>
                <input
                  id="purge-confirm"
                  type="text"
                  value={purgeConfirmationText}
                  onChange={(e) => setPurgeConfirmationText(e.target.value)}
                  placeholder="DELETE"
                />
              </div>
              <div className="modal-actions">
                <button onClick={handleClosePurgeModal} className="cancel-btn">Cancel</button>
                <button onClick={handleConfirmPurge} className="purge-btn" disabled={isPurging || purgeConfirmationText !== 'DELETE'}>
                  {isPurging ? 'Deleting...' : 'Permanently Delete Orders'}
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {showDuplicateModal && (
        <Modal isOpen={showDuplicateModal} onClose={() => setShowDuplicateModal(false)}>
          <h2>Confirm Duplicate Cleanup</h2>
          <p>
            Duplicate groups: <strong>{duplicatePreview?.duplicateGroups || 0}</strong><br />
            Duplicate docs to remove: <strong>{duplicatePreview?.duplicateDocs || 0}</strong>
          </p>
          <p>This action archives duplicate payloads and permanently deletes duplicate Firestore docs.</p>
          <div className="form-group">
            <label htmlFor="cleanup-confirm">Type <strong>CLEANUP</strong> to confirm.</label>
            <input
              id="cleanup-confirm"
              type="text"
              value={duplicateConfirmText}
              onChange={(e) => setDuplicateConfirmText(e.target.value)}
              placeholder="CLEANUP"
            />
          </div>
          <div className="modal-actions">
            <button onClick={() => setShowDuplicateModal(false)} className="cancel-btn">Cancel</button>
            <button
              onClick={handleExecuteDuplicateCleanup}
              className="purge-btn"
              disabled={isExecutingDuplicates || duplicateConfirmText !== 'CLEANUP'}
            >
              {isExecutingDuplicates ? 'Deleting...' : 'Run Duplicate Cleanup'}
            </button>
          </div>
        </Modal>
      )}

      {showArchiveRepairModal && (
        <Modal isOpen={showArchiveRepairModal} onClose={() => setShowArchiveRepairModal(false)}>
          <h2>Confirm BQ-Firestore Overlap Repair</h2>
          <p>
            Overlap orders found: <strong>{archiveRepairPreview?.overlapCount || 0}</strong>
          </p>
          <p>This action updates Firestore order docs with archived BigQuery data for all overlapped records.</p>
          <div className="form-group">
            <label htmlFor="repair-confirm">Type <strong>REPAIR</strong> to confirm.</label>
            <input
              id="repair-confirm"
              type="text"
              value={archiveRepairConfirmText}
              onChange={(e) => setArchiveRepairConfirmText(e.target.value)}
              placeholder="REPAIR"
            />
          </div>
          <div className="modal-actions">
            <button onClick={() => setShowArchiveRepairModal(false)} className="cancel-btn">Cancel</button>
            <button
              onClick={handleExecuteArchiveRepair}
              className="purge-btn"
              disabled={isExecutingArchiveRepair || archiveRepairConfirmText !== 'REPAIR'}
            >
              {isExecutingArchiveRepair ? 'Processing...' : 'Run Archive Repair'}
            </button>
          </div>
        </Modal>
      )}

      {showIdentityRepairModal && (
        <Modal isOpen={showIdentityRepairModal} onClose={() => setShowIdentityRepairModal(false)}>
          <h2>Confirm Identity Collision Repair</h2>
          <p>
            Candidates found: <strong>{identityRepairPreview?.candidateCount || 0}</strong><br />
            Moves: <strong>{identityRepairPreview?.moveCount || 0}</strong><br />
            Recalc-only: <strong>{identityRepairPreview?.recalcOnlyCount || 0}</strong>
          </p>
          <p>This action can move Firestore order docs to corrected canonical IDs and recalculate v3 fields.</p>
          <div className="form-group">
            <label htmlFor="identity-repair-confirm">Type <strong>REPAIR</strong> to confirm.</label>
            <input
              id="identity-repair-confirm"
              type="text"
              value={identityRepairConfirmText}
              onChange={(e) => setIdentityRepairConfirmText(e.target.value)}
              placeholder="REPAIR"
            />
          </div>
          <div className="modal-actions">
            <button onClick={() => setShowIdentityRepairModal(false)} className="cancel-btn">Cancel</button>
            <button
              onClick={handleExecuteIdentityRepair}
              className="purge-btn"
              disabled={isExecutingIdentityRepair || identityRepairConfirmText !== 'REPAIR'}
            >
              {isExecutingIdentityRepair ? 'Processing...' : 'Run Identity Repair'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default SystemActionsPage;
